import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Eye, Target, Sparkles, BookOpen, Briefcase, Check, Plus, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { useInterestTags } from "@/hooks/useInterests";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const objetivos = ["Jovem Aprendiz", "Estágio", "Primeiro emprego", "Mudança de área"];

const infoSteps = [
  {
    icon: Target,
    title: "Perfil que evolui",
    description: "Seu perfil começa em construção. Você vai adicionando experiências, habilidades e conquistas no seu ritmo.",
  },
  {
    icon: Eye,
    title: "Visibilidade controlada",
    description: "Você decide quando e como seu perfil aparece para empresas parceiras.",
  },
  {
    icon: Sparkles,
    title: "Trajetória, não currículo",
    description: "Valorizamos sua jornada, experiências informais e projetos pessoais.",
  },
];

export default function AlunoOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, isLoading: profileLoading, updateProfile } = useProfile();
  const { data: allTags, isLoading: tagsLoading } = useInterestTags();
  const next = new URLSearchParams(window.location.search).get("next");
  const [currentStep, setCurrentStep] = useState(0);
  const [bio, setBio] = useState("");
  const [objetivo, setObjetivo] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const totalSteps = 4; // info, bio, objetivo, interesses

  useEffect(() => {
    if (profile) {
      if (profile.bio) setBio(profile.bio);
      if (profile.objective) setObjetivo(profile.objective);
    }
  }, [profile]);

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : prev.length < 5
        ? [...prev, tagId]
        : prev
    );
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      await updateProfile.mutateAsync({
        bio: bio.trim() || null,
        objective: objetivo || null,
      });

      if (selectedTagIds.length > 0 && user) {
        await supabase.from("profile_interests").delete().eq("user_id", user.id);
        const rows = selectedTagIds.map((tag_id) => ({ user_id: user.id, tag_id }));
        await supabase.from("profile_interests").insert(rows);
      }

      navigate(next || "/aluno");
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (currentStep === totalSteps - 1) {
      handleFinish();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const canAdvance = () => {
    if (currentStep === 0) return true;
    if (currentStep === 1) return true; // bio is optional
    if (currentStep === 2) return true; // objetivo optional
    if (currentStep === 3) return true; // interests optional
    return true;
  };

  if (profileLoading || tagsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col px-6 py-8">
      {/* Progress */}
      <div className="flex gap-2 mb-8">
        {Array.from({ length: totalSteps }).map((_, step) => (
          <div
            key={step}
            className={`flex-1 h-1 rounded-full transition-colors ${
              step <= currentStep ? "bg-primary" : "bg-muted"
            }`}
          />
        ))}
      </div>

      <div className="flex-1 flex flex-col">
        {currentStep === 0 && (
          <motion.div key="step-0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex-1 flex flex-col">
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2">O que você precisa saber</h1>
            <p className="text-muted-foreground mb-8">Entenda como o Corre Digital funciona para você.</p>
            <div className="space-y-4 flex-1">
              {infoSteps.map((step, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  className="flex gap-4 p-4 bg-card rounded-2xl border border-border">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{step.title}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              className="mt-8 p-4 bg-accent/10 rounded-2xl border border-accent/20">
              <p className="text-sm text-center text-foreground font-medium">
                "Você não precisa estar pronto. Precisa estar comprometido."
              </p>
            </motion.div>
          </motion.div>
        )}

        {currentStep === 1 && (
          <motion.div key="step-1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex-1 flex flex-col">
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Conte sobre você</h1>
            <p className="text-muted-foreground mb-6">Uma bio curta que mostre quem você é e o que te motiva.</p>
            <Textarea
              placeholder="Ex: Sou estudante de administração, apaixonado por tecnologia. Busco minha primeira oportunidade na área digital..."
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={5}
              className="resize-none mb-2"
              maxLength={300}
            />
            <p className="text-xs text-muted-foreground">{bio.length}/300 caracteres</p>
            <p className="text-xs text-muted-foreground mt-4">Você pode pular e preencher depois.</p>
          </motion.div>
        )}

        {currentStep === 2 && (
          <motion.div key="step-2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex-1 flex flex-col">
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Qual seu objetivo?</h1>
            <p className="text-muted-foreground mb-6">Isso ajuda a personalizar sua experiência.</p>
            <div className="space-y-3">
              {objetivos.map((obj) => (
                <button
                  key={obj}
                  onClick={() => setObjetivo(objetivo === obj ? "" : obj)}
                  className={`w-full text-left p-4 rounded-2xl border transition-all ${
                    objetivo === obj
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border bg-card hover:border-primary/30"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      objetivo === obj ? "bg-primary text-primary-foreground" : "bg-muted"
                    }`}>
                      {objetivo === obj && <Check className="w-4 h-4" />}
                    </div>
                    <span className="text-sm font-medium text-foreground">{obj}</span>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {currentStep === 3 && (
          <motion.div key="step-3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex-1 flex flex-col">
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Seus interesses</h1>
            <p className="text-muted-foreground mb-2">Escolha até 5 áreas que te interessam.</p>
            <p className="text-xs text-muted-foreground mb-6">{selectedTagIds.length}/5 selecionados</p>

            <div className="flex flex-wrap gap-2">
              {(allTags ?? []).map((tag) => {
                const selected = selectedTagIds.includes(tag.id);
                return (
                  <Badge
                    key={tag.id}
                    variant={selected ? "interest-selected" : "interest"}
                    className="cursor-pointer"
                    onClick={() => toggleTag(tag.id)}
                  >
                    {selected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    {tag.name}
                  </Badge>
                );
              })}
            </div>

            {(!allTags || allTags.length === 0) && (
              <p className="text-sm text-muted-foreground mt-4">Nenhum interesse disponível.</p>
            )}
          </motion.div>
        )}

        {/* CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8">
          <Button variant="hero" size="xl" className="w-full" onClick={handleNext} disabled={!canAdvance() || saving}>
            {saving ? (
              <><Loader2 className="w-5 h-5 animate-spin" />Salvando...</>
            ) : currentStep === totalSteps - 1 ? (
              <>Começar minha jornada<ArrowRight className="w-5 h-5" /></>
            ) : (
              <>Continuar<ArrowRight className="w-5 h-5" /></>
            )}
          </Button>
          {currentStep > 0 && currentStep < totalSteps - 1 && (
            <Button variant="ghost" className="w-full mt-2" onClick={handleNext}>
              Pular por agora
            </Button>
          )}
        </motion.div>
      </div>
    </div>
  );
}
